import { useState, useEffect, useRef, useCallback } from 'react';
import {
  X,
  Plus,
  Pencil,
  Trash2,
  Image as ImageIcon,
  FileText,
  Loader2,
  Upload,
  ExternalLink,
  Calendar,
  Check,
} from 'lucide-react';
import {
  BlogService,
  slugify,
  markdownToHtml,
  type BlogPost,
  type BlogImage,
} from '../../services/blogService';

interface BlogManagerModalProps {
  businessId: string;
  businessName: string;
  onClose: () => void;
}

const emptyDraft = {
  id: undefined as string | undefined,
  title: '',
  slug: '',
  body_markdown: '',
  excerpt: '',
  meta_description: '',
  featured_image_url: null as string | null,
  status: 'draft' as 'draft' | 'published',
  published_at: null as string | null,
};

function formatPostDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function postBadge(post: BlogPost): { label: string; className: string } {
  if (post.status === 'draft') return { label: 'Draft', className: 'bg-gray-100 text-gray-600 border-gray-200' };
  if (post.published_at && new Date(post.published_at).getTime() > Date.now()) {
    return { label: 'Scheduled', className: 'bg-amber-50 text-amber-800 border-amber-200' };
  }
  return { label: 'Published', className: 'bg-green-50 text-green-800 border-green-200' };
}

export default function BlogManagerModal({ businessId, businessName, onClose }: BlogManagerModalProps) {
  const [tab, setTab] = useState<'posts' | 'images'>('posts');
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [images, setImages] = useState<BlogImage[]>([]);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [pickingFeatured, setPickingFeatured] = useState(false);

  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, i] = await Promise.all([BlogService.listPosts(businessId), BlogService.listImages(businessId)]);
      setPosts(p);
      setImages(i);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    void load();
  }, [load]);

  const startNewPost = () => {
    setDraft(emptyDraft);
    setSlugTouched(false);
    setShowPreview(false);
    setEditing(true);
  };

  const startEditPost = (post: BlogPost) => {
    setDraft({
      id: post.id,
      title: post.title,
      slug: post.slug,
      body_markdown: post.body_markdown,
      excerpt: post.excerpt || '',
      meta_description: post.meta_description || '',
      featured_image_url: post.featured_image_url,
      status: post.status,
      published_at: post.published_at,
    });
    setSlugTouched(true);
    setShowPreview(false);
    setEditing(true);
  };

  const handleSave = async (publish: 'draft' | 'published') => {
    setSaving(true);
    try {
      const result = await BlogService.savePost({
        id: draft.id,
        business_id: businessId,
        title: draft.title,
        slug: draft.slug,
        body_markdown: draft.body_markdown,
        excerpt: draft.excerpt,
        meta_description: draft.meta_description,
        featured_image_url: draft.featured_image_url,
        status: publish,
        published_at: draft.published_at,
      });
      if (!result.success) {
        alert(result.message);
        return;
      }
      setEditing(false);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePost = async (post: BlogPost) => {
    if (!window.confirm(`Delete "${post.title}"? This removes it from the live site immediately.`)) return;
    const result = await BlogService.deletePost(post.id);
    if (!result.success) {
      alert(result.message);
      return;
    }
    await load();
  };

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const result = await BlogService.uploadImage(businessId, file);
        if (!result.success) alert(`${file.name}: ${result.message}`);
      }
      await load();
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteImage = async (image: BlogImage) => {
    if (!window.confirm('Delete this image from the library?')) return;
    const result = await BlogService.deleteImage(image);
    if (!result.success) {
      alert(result.message);
      return;
    }
    await load();
  };

  const scheduledInputValue = draft.published_at
    ? new Date(new Date(draft.published_at).getTime() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16)
    : '';

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-3xl sm:rounded-2xl rounded-t-2xl shadow-xl flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="min-w-0">
            <h2 className="text-[16px] font-bold text-gray-900 truncate">Blog — {businessName}</h2>
            <p className="text-[12px] text-gray-400">
              {posts.length} post{posts.length === 1 ? '' : 's'} · {images.length} image{images.length === 1 ? '' : 's'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs (hidden while editing) */}
        {!editing && (
          <div className="flex gap-2 px-4 pt-3">
            {(['posts', 'images'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold rounded-lg transition-all ${
                  tab === t ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {t === 'posts' ? <FileText className="h-3.5 w-3.5" /> : <ImageIcon className="h-3.5 w-3.5" />}
                {t === 'posts' ? 'Posts' : 'Image library'}
              </button>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : editing ? (
            /* ── Post editor ── */
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Post title"
                value={draft.title}
                onChange={(e) => {
                  const title = e.target.value;
                  setDraft((d) => ({ ...d, title, slug: slugTouched ? d.slug : slugify(title) }));
                }}
                className="w-full text-[15px] font-semibold rounded-lg border border-gray-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              />
              <div className="flex items-center gap-2">
                <span className="text-[12px] text-gray-400 shrink-0">/blog/</span>
                <input
                  type="text"
                  placeholder="url-slug"
                  value={draft.slug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    setDraft((d) => ({ ...d, slug: slugify(e.target.value) }));
                  }}
                  className="flex-1 text-[13px] font-mono rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                />
              </div>

              {/* Featured image */}
              <div className="rounded-lg border border-gray-200 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide">Featured image</span>
                  <button
                    onClick={() => setPickingFeatured((v) => !v)}
                    className="text-[12px] font-semibold text-blue-600"
                  >
                    {pickingFeatured ? 'Close' : draft.featured_image_url ? 'Change' : 'Choose from library'}
                  </button>
                </div>
                {draft.featured_image_url && !pickingFeatured ? (
                  <img src={draft.featured_image_url} alt="" className="h-28 rounded-lg object-cover" />
                ) : null}
                {pickingFeatured ? (
                  images.length === 0 ? (
                    <p className="text-[12px] text-gray-400">No images in the library yet — upload some in the Image library tab.</p>
                  ) : (
                    <div className="grid grid-cols-4 gap-2">
                      {images.map((img) => (
                        <button
                          key={img.id}
                          onClick={() => {
                            setDraft((d) => ({ ...d, featured_image_url: img.public_url }));
                            setPickingFeatured(false);
                          }}
                          className={`relative rounded-lg overflow-hidden border-2 ${
                            draft.featured_image_url === img.public_url ? 'border-blue-500' : 'border-transparent'
                          }`}
                        >
                          <img src={img.public_url} alt={img.alt_text || ''} className="h-16 w-full object-cover" />
                          {draft.featured_image_url === img.public_url && (
                            <span className="absolute top-1 right-1 bg-blue-600 rounded-full p-0.5">
                              <Check className="h-3 w-3 text-white" />
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )
                ) : null}
              </div>

              {/* Body */}
              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
                  <span className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide">
                    Content (markdown: # headings, **bold**, - lists, [links](url))
                  </span>
                  <button onClick={() => setShowPreview((v) => !v)} className="text-[12px] font-semibold text-blue-600">
                    {showPreview ? 'Edit' : 'Preview'}
                  </button>
                </div>
                {showPreview ? (
                  <div
                    className="prose prose-sm max-w-none p-3 min-h-[220px]"
                    // Rendered from the editor's own markdown — same HTML the live site receives.
                    dangerouslySetInnerHTML={{ __html: markdownToHtml(draft.body_markdown) }}
                  />
                ) : (
                  <textarea
                    value={draft.body_markdown}
                    onChange={(e) => setDraft((d) => ({ ...d, body_markdown: e.target.value }))}
                    placeholder={'## Why ceramic coating matters\n\nYour content here...\n\n- Point one\n- Point two'}
                    className="w-full min-h-[220px] p-3 text-[13px] font-mono focus:outline-none resize-y"
                  />
                )}
              </div>

              <textarea
                placeholder="Excerpt (shown in the blog list — 1-2 sentences)"
                value={draft.excerpt}
                onChange={(e) => setDraft((d) => ({ ...d, excerpt: e.target.value }))}
                className="w-full text-[13px] rounded-lg border border-gray-200 px-3 py-2 min-h-[52px] focus:outline-none focus:ring-2 focus:ring-blue-500/30 resize-y"
              />
              <input
                type="text"
                placeholder="Meta description (SEO — max ~155 characters)"
                value={draft.meta_description}
                maxLength={170}
                onChange={(e) => setDraft((d) => ({ ...d, meta_description: e.target.value }))}
                className="w-full text-[13px] rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              />

              {/* Schedule */}
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-400 shrink-0" />
                <input
                  type="datetime-local"
                  value={scheduledInputValue}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      published_at: e.target.value ? new Date(e.target.value).toISOString() : null,
                    }))
                  }
                  className="text-[13px] rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                />
                <span className="text-[11px] text-gray-400">
                  Leave empty to publish immediately; set a future time to schedule.
                </span>
              </div>

              <div className="flex flex-wrap gap-2 pt-1">
                <button
                  onClick={() => handleSave('published')}
                  disabled={saving || !draft.title.trim()}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[13px] font-semibold text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                >
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                  {draft.published_at && new Date(draft.published_at).getTime() > Date.now() ? 'Schedule' : 'Publish'}
                </button>
                <button
                  onClick={() => handleSave('draft')}
                  disabled={saving || !draft.title.trim()}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[13px] font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
                >
                  Save draft
                </button>
                <button
                  onClick={() => setEditing(false)}
                  disabled={saving}
                  className="inline-flex items-center px-4 py-2 rounded-lg text-[13px] font-medium text-gray-500 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : tab === 'posts' ? (
            /* ── Post list ── */
            <div className="space-y-2">
              <button
                onClick={startNewPost}
                className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-[13px] font-semibold text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-4 w-4" /> New post
              </button>
              {posts.length === 0 ? (
                <p className="text-center text-[13px] text-gray-400 py-10">No posts yet.</p>
              ) : (
                posts.map((post) => {
                  const badge = postBadge(post);
                  return (
                    <div key={post.id} className="flex items-center gap-3 rounded-xl border border-gray-200 p-3">
                      {post.featured_image_url ? (
                        <img src={post.featured_image_url} alt="" className="h-12 w-12 rounded-lg object-cover shrink-0" />
                      ) : (
                        <div className="h-12 w-12 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                          <FileText className="h-5 w-5 text-gray-300" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-[14px] font-semibold text-gray-900 truncate">{post.title}</p>
                        <p className="text-[12px] text-gray-400 truncate">
                          /blog/{post.slug} · {formatPostDate(post.published_at)}
                        </p>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border shrink-0 ${badge.className}`}>
                        {badge.label}
                      </span>
                      <button onClick={() => startEditPost(post)} className="p-2 text-gray-400 hover:text-blue-600">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDeletePost(post)} className="p-2 text-gray-400 hover:text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          ) : (
            /* ── Image library ── */
            <div className="space-y-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => handleUpload(e.target.files)}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-[13px] font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Upload images
              </button>
              {images.length === 0 ? (
                <p className="text-center text-[13px] text-gray-400 py-10">
                  No images yet. Upload photos of this business's work — the AI will use them for auto-blogs later.
                </p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {images.map((img) => (
                    <div key={img.id} className="relative group rounded-lg overflow-hidden border border-gray-200">
                      <img src={img.public_url} alt={img.alt_text || ''} className="h-24 w-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <a href={img.public_url} target="_blank" rel="noopener noreferrer" className="p-1.5 bg-white/90 rounded-lg">
                          <ExternalLink className="h-3.5 w-3.5 text-gray-700" />
                        </a>
                        <button onClick={() => handleDeleteImage(img)} className="p-1.5 bg-white/90 rounded-lg">
                          <Trash2 className="h-3.5 w-3.5 text-red-600" />
                        </button>
                      </div>
                      {img.used_in_post_id && (
                        <span className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/60 text-white text-[10px] rounded">
                          used
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
